/**
 * Types and render helpers of the check-inventory modal (`./CheckInventoryModal.tsx`): the
 * caller-facing props and result, the props shared by the desktop and mobile views, the batch
 * shape returned by Items `items/batches`, the "not available locally" message and the mobile batch
 * row.
 */
import { UserProfile } from "@ryerson/frontend.application";
import { Country, Product, getUsersDefaultUomValue } from "@ryerson/frontend.common";
import React from "react";
import { CheckInventoryLabels } from "./CheckInventoryModalContent";
import { Option } from "@ryerson/frontend.form-controls";
import { ITheme } from "@ryerson/frontend.theme";
import { Typography } from "@ryerson/frontend.typography";
import { PhoneLink } from "@ryerson/frontend.navigation";
import { Flex } from "@ryerson/frontend.layout";
import { truncateNumber } from "@components/Helper/common";
import { getTotalQuantityInLB } from "@components/Shared/MoreYouBuyMoreYouSave";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { UOM, Company } from "@ryerson/common.enums";
import { PhoneNumbers } from "@ryerson/common.constants";
import { PhoneNumber } from "@ryerson/common.models";

const Divider = styled.div`
	opacity: 0.1;
	${(props: any) => {
		const { color } = props;
		return css`
			border-bottom: 1px solid ${color};
		`;
	}}
`;

/**
 * What the modal reports to the caller's `onUpdate` when it closes. `index` is the caller's row
 * index passed in, unchanged; `hasBeenChanged` is true when the quantity or UOM differ from what
 * the modal was opened with.
 */
export type InventoryModalResponse = {
	index: number;
	ryersonSku: string;
	quantity: number;
	quantityUom: string;
	hasBeenChanged: boolean;
};

/** Props of `./CheckInventoryModal.tsx`, set by the screen that opens it. */
export interface CheckInventoryModalType {
	index: number; // It will require in order to deal with Datatable Components
	showCheckInventoryModal: boolean;
	setShowCheckInventoryModal: (value: boolean) => void;
	quantity: number;
	qtyUom: UOM;
	priceUom: UOM;
	product: Product;
	onUpdate: (response: InventoryModalResponse) => void;
	/**
	 * False skips both API calls and shows the "not available locally" message. Callers pass
	 * whether the line's delivery-times response listed an available plant.
	 */
	showBatchInformation: boolean;
}

/**
 * Props the modal passes to its desktop and mobile views. `batchHandleOnChangeQty`,
 * `batchUpdateQuantityUom`, `qtyUomDropdown` and `batchDisplayQuantity` feed the quantity input,
 * which both views have commented out; that input is dead code, to be removed with these props.
 * `isClosable` is read by the desktop view only.
 */
export interface CheckInventoryModalProps {
	showCheckInventoryModal: boolean;
	setShowCheckInventoryModal: (value: boolean) => void;
	content: CheckInventoryLabels;
	batches: Batch[];
	qtyUomDropdown: Option[];
	batchQtyUom: string;
	batchPriceUom: string;
	batchDisplayQuantity: number;
	batchUpdateQuantityUom: (value: UOM) => void;
	batchHandleOnChangeQty: (value: any) => void;
	product: Product;
	fetching: boolean;
	handleOnClose: () => void;
	showItemNotAvailableLocallyMessage: boolean;
	isClosable?: boolean;
}

/**
 * One batch from Items `items/batches`. The modal numbers the rows from 1 into `id` for the table
 * (an `id` in the API item overrides it). `quantityAvailable` is shown in the requested quantity
 * UOM and converted to pounds for the "Available (lbs)" column.
 */
export type Batch = {
	id: number;
	batchNumber: number | string;
	plantNumber: number | string;
	quantityAvailable: number | string;
	uom: UOM;
};

/** Opens the LiveChat widget when the page has loaded it; does nothing otherwise. */
export const onClickOpenChatWindow = () => {
	if (window && (window as any).LiveChatWidget) {
		(window as any).LiveChatWidget.call("maximize");
	}
};

/**
 * The single empty row shown before batches arrive; its empty `quantityAvailable` makes the cell
 * show "Loading...".
 */
export const defaultBatchResponse: Batch = {
	id: 1,
	batchNumber: "",
	plantNumber: "",
	quantityAvailable: "",
	uom: UOM.LB,
};

/**
 * Sets the quantity-UOM options for the product and the user (`getUsersDefaultUomValue`). Despite
 * the name, it sets no price-UOM options.
 */
export const setUOMAndPriceUOMDropDown = (
	productItem: Product,
	profile: UserProfile,
	setQtyUOMDropdown: React.Dispatch<React.SetStateAction<Option[]>>
): void => {
	const { qtyUomOption } = getUsersDefaultUomValue(productItem, profile);
	setQtyUOMDropdown(qtyUomOption);
};

/**
 * The "not available locally" message with a "Chat with us" link and the brand's phone number for
 * the user's country (`PhoneNumbers.PHONE_NUMBERS[NEXT_PUBLIC_COMPANY][country]`).
 */
export const renderItemNotAvailableLocallyMessage = (
	content: CheckInventoryLabels,
	theme: ITheme,
	country: Country
): JSX.Element => {
	const company: Company = (process.env.NEXT_PUBLIC_COMPANY || "ryerson") as Company;
	const phoneNumber: PhoneNumber = PhoneNumbers.PHONE_NUMBERS[company][country];
	return (
		<>
			<Typography size="md">
				{content.itemNotAvailableLocallyMessage}&nbsp;
				<div
					css={{ display: "inline-block", color: theme.colors.primary.link }}
					onClick={onClickOpenChatWindow}
				>
					{content.chatWithUs}
				</div>
				&nbsp;
				{content.or}&nbsp;
				<PhoneLink
					type="primary"
					number={phoneNumber.value}
					formatter={phoneNumber.number}
				/>
				&nbsp;
				{content.errorMessage2}
			</Typography>
		</>
	);
};

/**
 * One batch row of the mobile view: batch number, quantity available (or "Loading..." while
 * fetching) and that quantity in pounds. The desktop view renders the same three columns through
 * `./CheckInventoryModalTable/CheckInventoryModalTableConfig.tsx`.
 */
export const renderMobileBatchData = (
	index: number,
	fetching: boolean,
	batch: Batch,
	quantityUom: string,
	priceUom: string,
	product: Product,
	hrColor: string,
	content: CheckInventoryLabels
): JSX.Element => {
	return (
		<Flex direction="column" key={index}>
			<Flex
				css={{ height: "52px", padding: "0 10px" }}
				justifyContent="space-between"
				alignItems="center"
			>
				<Typography size="md">{batch.batchNumber && batch.batchNumber}</Typography>
				<Typography size="md">
					{!fetching && batch.quantityAvailable ? (
						truncateNumber(Number(batch.quantityAvailable), 2)
					) : (
						<Flex>
							<Typography>{content.loading}</Typography>
						</Flex>
					)}
				</Typography>
				<Typography size="md">
					{batch.quantityAvailable &&
						truncateNumber(
							Number(
								getTotalQuantityInLB(
									quantityUom as UOM,
									Number(batch.quantityAvailable),
									product,
									priceUom as UOM
								)
							),
							2
						)}
				</Typography>
			</Flex>

			<Divider {...{ color: hrColor }} />
		</Flex>
	);
};
